import { useEffect, useState } from 'react';
import { useRoute, useSession, PlusIcon } from '@huaca/ui';
import { listInstances } from '../api';
import type { Instance, InstanceSummary } from '../types';
import { Skeleton } from '../Skeleton';
import { InstanceDetail } from './InstanceDetail';
import { StartOnboardingModal } from './StartOnboardingModal';
import { StatusBadge } from './StatusBadge';

/**
 * The professional dashboard: a split screen — the org's clients/onboardings at the inline-start
 * (right, in RTL) and the selected onboarding's detail at the inline-end.
 */
export function Dashboard({ initialSelectedId }: { initialSelectedId?: string } = {}) {
  const { client, can } = useSession();
  const { navigate } = useRoute();
  const [instances, setInstances] = useState<InstanceSummary[] | null>(null);
  // Seeds the selection — set when we return from the snapshot editor (/instances/:id) so the client
  // the pro was editing stays selected instead of resetting to the top of the list.
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // View gate, not edit: agents (claro:template:read) reach the read-only template list;
  // TemplateList keeps every edit/create/archive action behind claro:template:edit.
  const mayViewTemplates = can('claro:template:read');
  const mayCreate = can('claro:instance:create');

  useEffect(() => {
    let live = true;
    listInstances(client)
      .then((list) => {
        if (!live) return;
        setInstances(list);
        setSelectedId((prev) => prev ?? list[0]?._id ?? null);
      })
      .catch(() => live && setError('לא ניתן לטעון את רשימת המפרטים.'));
    return () => {
      live = false;
    };
  }, [client]);

  /** `Instance` extends `InstanceSummary`, so the fresh instance drops straight into the list. */
  function onCreated(instance: Instance) {
    setInstances((prev) => [instance, ...(prev ?? [])]);
    setSelectedId(instance._id);
    setModalOpen(false);
  }

  return (
    <div className="claro-dashboard claro-page" data-testid="claro-dashboard">
      <div className="claro-page-head">
        <h1>מפרטי לקוחות</h1>
        <div className="claro-head-actions">
          {mayViewTemplates && (
            <button type="button" className="btn-ghost" onClick={() => navigate('/templates')} data-testid="manage-templates">
              תבניות
            </button>
          )}
          {mayCreate && (
            <button type="button" className="btn-primary" onClick={() => setModalOpen(true)} data-testid="start-onboarding">
              <PlusIcon />
              תהליך חדש
            </button>
          )}
        </div>
      </div>

      {error && <p role="alert">{error}</p>}

      <div className="claro-split">
        <aside className="claro-list-pane" aria-label="לקוחות">
          {!instances ? (
            <Skeleton lines={5} />
          ) : instances.length === 0 ? (
            <div className="huaca-state" data-testid="no-instances">
              <h2>אין עדיין מפרטים</h2>
              <p>התחילו תהליך ראשון — תקבלו קישור מאובטח לשליחה ללקוח.</p>
            </div>
          ) : (
            <ul className="claro-client-list">
              {instances.map((i) => (
                <li key={i._id}>
                  <button
                    type="button"
                    className={i._id === selectedId ? 'claro-client is-selected' : 'claro-client'}
                    onClick={() => setSelectedId(i._id)}
                    data-testid={`client-${i._id}`}
                  >
                    <span className="claro-client-name">{i.client.fullName}</span>
                    {/* The element stays in RTL flow (so its logical margins resolve correctly);
                        only the LTR text is isolated with <bdi>. */}
                    <span className="claro-client-phone">
                      <bdi>{i.client.phone}</bdi>
                    </span>
                    <StatusBadge status={i.status} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="claro-detail-pane">
          {selectedId ? (
            <InstanceDetail instanceId={selectedId} />
          ) : (
            <p className="claro-hint">בחרו לקוח מהרשימה כדי לראות את הפרטים.</p>
          )}
        </div>
      </div>

      {modalOpen && <StartOnboardingModal onClose={() => setModalOpen(false)} onCreated={onCreated} />}
    </div>
  );
}
